import React from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch, useSoftDeployAccess } from '../hooks';
import { setAssistantOpen } from 'Store/actions/AppActions';
import { RootState } from 'Store/index';
import { useSelf } from 'App/components/hooks/appHooks';
import ErrorBoundary from 'App/components/ErrorBoundary';
import ShadcnScope from 'App/shadcn/components/ShadcnScope';
import SnapHotspot from 'App/shadcn/pages/Assistant/SnapHotspot';
import { useDraggableChat } from './useDraggableChat';

const AssistantShell = React.lazy(() => import('App/shadcn/pages/Assistant/AssistantShell'));

const AssistantDrawer = () => {
	const hasFeature = useSoftDeployAccess('AI_ASSISTANT');
	const self = useSelf();
	const assistantOpen = useSelector(({ App }: RootState) => App.assistantOpen);
	const dispatch = useAppDispatch();

	const {
		position,
		width,
		height,
		isDragging,
		isResizing,
		resizeDirection,
		snapHotspot,
		containerWidth,
		containerRef,
		handleMouseDown,
		handleDoubleClick,
		isMaximized,
		getResizeDirection,
		getResizeCursor
	} = useDraggableChat({
		isOpen: assistantOpen,
		onToggle: () => dispatch(setAssistantOpen(!assistantOpen)),
		onClose: () => dispatch(setAssistantOpen(false))
	});

	if (!hasFeature || !assistantOpen) return null;

	return (
		<ErrorBoundary>
			<SnapHotspot position="left" width={containerWidth} isVisible={isDragging && snapHotspot === 'left'} />
			<SnapHotspot position="right" width={containerWidth} isVisible={isDragging && snapHotspot === 'right'} />
			<ShadcnScope
				ref={containerRef}
				style={{
					position: 'fixed',
					zIndex: 10010,
					left: `${position.x}px`,
					top: `${position.y}px`,
					...(width !== null && { width: `${width}px` }),
					...(height !== null && { height: `${height}px` }),
					minWidth: 420,
					minHeight: 500,
					borderRadius: 16,
					boxShadow: '0 10px 40px -10px rgba(15, 23, 42, 0.25), 0 4px 12px -4px rgba(15, 23, 42, 0.15)',
					cursor: isResizing ? getResizeCursor(resizeDirection) : undefined,
					transition:
						isDragging || isResizing
							? 'none'
							: 'width 0.3s ease, height 0.3s ease, top 0.3s ease, left 0.3s ease'
				}}
				onMouseDown={e => {
					const divEvent = e as unknown as React.MouseEvent<HTMLDivElement>;
					const resizeDir = getResizeDirection(divEvent);
					if (resizeDir) handleMouseDown(divEvent);
				}}
				onMouseMove={e => {
					const divEvent = e as unknown as React.MouseEvent<HTMLDivElement>;
					if (!isDragging && !isResizing && containerRef.current) {
						const resizeDir = getResizeDirection(divEvent);
						containerRef.current.style.cursor = resizeDir ? getResizeCursor(resizeDir) : '';
					}
				}}
			>
				<React.Suspense fallback={null}>
					<AssistantShell
						onClose={() => dispatch(setAssistantOpen(false))}
						onToggleMaximize={handleDoubleClick}
						isMaximized={isMaximized}
						isDragging={isDragging}
						isResizing={isResizing}
						onHeaderMouseDown={handleMouseDown as (e: React.MouseEvent<HTMLElement>) => void}
						getResizeDirection={getResizeDirection as (e: React.MouseEvent<HTMLElement>) => string | null}
						selfId={self?.id}
					/>
				</React.Suspense>
			</ShadcnScope>
		</ErrorBoundary>
	);
};

export default AssistantDrawer;
